import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  CircularProgress,
  InputAdornment,
  Checkbox,
  Popover,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import moment from "moment";
import { Search } from "@mui/icons-material";
import {
  FaChevronDown,
  FaCircle,
  FaSort,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import { AiOutlineDownload } from "react-icons/ai";
import { IoCalendarClearOutline, IoPrintOutline } from "react-icons/io5";
import { MdSort } from "react-icons/md";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  exportXlsxCardStock,
  fetchCardStockNew,
  getPrintCardstock,
} from "../../../../services/InventoryAPI";
import { inventoryStatus } from "../../../constants/constant";
import useDebounce from "../../../hooks/useDebounce";
import ErrorPopup from "../../../common/particles.jsx/ErrorPopup";
import LoadingBackdrop from "../../../common/particles.jsx/LoadingBackdrop";
import PrintCardStock from "./PrintCardStock";
import { getPreviewInvoice } from "../../../../services/ReportingApi";
import JumpPagePagination from "../../../common/particles.jsx/JumpPagePagination";

const toApiDate = (date) => moment(date).format("YYYY-MM-DD");

const PERIOD = [
  { value: 0, name: "Semua" },
  { value: 1, name: "Minggu Berjalan" },
  { value: 2, name: "Minggu Lalu" },
  { value: 3, name: "Bulan Berjalan" },
  { value: 4, name: "Bulan Lalu" },
  { value: 5, name: "Tahun Berjalan" },
  { value: 6, name: "Tahun Lalu" },
  { value: 7, name: "Pilih Tanggal" },
];

const SORTBY = [
  { value: 0, name: "Tidak tersortir" },
  { value: 1, name: "A-Z" },
  { value: 2, name: "Z-A" },
  { value: 3, name: "Produk terbaru ditambahkan" },
  { value: 4, name: "Produk terlama ditambahkan" },
];

const CardStockNew = ({ isWarehouse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const [selectedStock, setSelectedStock] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [reqBody, setReqBody] = useState({
    warehouse: isWarehouse ? isWarehouse : false,
    product_name: "",
    start_date: "",
    end_date: "",
    period: 0,
    sort: 3,
    limit: 10,
    offset: 1,
  });
  const [anchorElSort, setAnchorElSort] = useState(null);
  const [anchorElPeriod, setAnchorElPeriod] = useState(null);
  const [anchorElExport, setAnchorElExport] = useState(null);
  const [anchorElPrint, setAnchorElPrint] = useState(null);
  const [isPrint, setIsPrint] = useState(false);

  const debounce = useDebounce(reqBody.product_name, 500);

  const stringToColour = (str) => {
    if (!str) return;
    let hash = 0;
    str.split("").forEach((char) => {
      hash = char.charCodeAt(0) + ((hash << 5) - hash);
    });
    let colour = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      colour += value.toString(16).padStart(2, "0");
    }
    return colour;
  };

  const handleExport = () => {
    setAnchorElExport(null);
    mutateXlsx({
      id: selectedStock,
      sort: reqBody.period,
      start_date: reqBody.start_date ? toApiDate(reqBody.start_date) : "",
      end_date: reqBody.end_date ? toApiDate(reqBody.end_date) : "",
      warehouse: isWarehouse ? isWarehouse : false,
    });
  };

  const handleExportAll = () => {
    setAnchorElExport(null);
    mutateXlsx({
      sort: reqBody.period,
      start_date: reqBody.start_date ? toApiDate(reqBody.start_date) : "",
      end_date: reqBody.end_date ? toApiDate(reqBody.end_date) : "",
      warehouse: isWarehouse ? isWarehouse : false,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      setSelectedStock([]);
      setAnchorElPrint(null);
      setIsPrint(false);
    },
    documentTitle: "Kartu Stok",
  });

  const handleXlsxData = (xlsxData) => {
    const url = xlsxData;
    const a = document.createElement("a");
    a.download = "card-stock-history.xlsx";
    a.href = url;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const { mutate: mutateXlsx, isLoading: isLoadingXlsx } = useMutation({
    mutationFn: exportXlsxCardStock,
    onSuccess: (data) => handleXlsxData(data),
    onError: (err) =>
      setErrorMessage(err.message.id ? err.message.id : err.message),
  });

  const {
    mutate: mutateCardStockData,
    data: cardStockData,
    isLoading: isCardStockDataLoading,
  } = useMutation({
    mutationFn: fetchCardStockNew,
  });

  const {
    mutate: mutatePrint,
    data: dataPrint,
    isLoading: isLoadingPrint,
  } = useMutation({
    mutationFn: getPrintCardstock,
    onSuccess: () => {
      setTimeout(() => {
        handlePrint();
      }, 500);
    },
    onError: (err) => {
      setAnchorElPrint(null);
      setErrorMessage(err.message.id ? err.message.id : err.message);
      setIsPrint(false);
    },
  });

  const { data: dataLabel } = useQuery({
    queryKey: ["label-print"],
    queryFn: getPreviewInvoice,
  });

  useEffect(() => {
    mutateCardStockData({
      ...reqBody,
      product_name: debounce,
      offset: reqBody.offset,
      limit: reqBody.limit,
      period: reqBody.period,
    });
  }, [
    reqBody.sort,
    reqBody.period,
    reqBody.offset,
    reqBody.limit,
    reqBody.end_date,
    debounce,
    mutateCardStockData,
    location.pathname,
  ]);

  return (
    <div>
      <LoadingBackdrop isLoading={isLoadingXlsx || isPrint} />

      <div className="bg-white rounded-lg p-2">
        <div className="w-full lg:flex items-center justify-between gap-2">
          <div className="w-full flex items-center gap-2 mb-2">
            <div
              className={`w-full lg:w-fit border-2 rounded-md flex items-center justify-between gap-2 md:gap-4 p-2 cursor-pointer hover:bg-gray-100`}
              onClick={(e) => setAnchorElPeriod(e.currentTarget)}
            >
              <div className="flex gap-2 md:gap-4 items-center">
                <IoCalendarClearOutline />
                <p>
                  {reqBody.period === 0
                    ? "Semua Periode"
                    : reqBody.period === 7 &&
                      reqBody.start_date !== "" &&
                      reqBody.end_date !== ""
                    ? moment(reqBody.start_date).format("DD-MM-YYYY") +
                      " s/d " +
                      moment(reqBody.end_date).format("DD-MM-YYYY")
                    : PERIOD.find((item) => item.value === reqBody.period)
                        ?.name}
                </p>
              </div>
              <FaChevronDown />
            </div>
            <div
              className={`w-fit border-2 rounded-md flex items-center justify-center p-2 cursor-pointer  hover:bg-gray-100`}
              onClick={(e) => setAnchorElSort(e.currentTarget)}
            >
              <MdSort className="text-[18px]" />
            </div>
          </div>
          <div className="w-full flex items-center gap-2 mb-2">
            <TextField
              value={reqBody.product_name}
              onChange={(e) =>
                setReqBody((prevValue) => ({
                  ...prevValue,
                  product_name: e.target.value,
                }))
              }
              fullWidth
              placeholder="Cari nama produk atau distributor"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <button
              type="button"
              className="text-[12px] border-2 border-[#94C679] flex items-center gap-2 text-[#94C679] bg-[#F1FAF0] hover:bg-gray-300 rounded-md w-fit p-2 whitespace-nowrap"
              onClick={(e) => setAnchorElPrint(e.currentTarget)}
            >
              <IoPrintOutline />
              Print
            </button>
            <button
              type="button"
              className="text-[12px] flex items-center gap-2 text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-fit p-2 whitespace-nowrap"
              onClick={(e) => setAnchorElExport(e.currentTarget)}
            >
              <AiOutlineDownload />
              Export
            </button>
          </div>
        </div>

        <div className="flex w-full mb-4 mt-2">
          <div className="w-full">
            <div className="block overflow-x-auto w-full">
              <table className="w-full overflow-x-auto-md border-collapse">
                <thead className="bg-[#F7F8F9]">
                  <th className="p-2 text-left w-fit">
                    <Checkbox
                      disabled={isCardStockDataLoading || !cardStockData}
                      checked={
                        cardStockData &&
                        cardStockData.data &&
                        cardStockData.count > 0 &&
                        selectedStock.length > 0
                          ? selectedStock.length === cardStockData.data.length
                          : false
                      }
                      onChange={(e, checked) => {
                        setSelectedStock(
                          checked
                            ? cardStockData &&
                              !isCardStockDataLoading &&
                              cardStockData.data
                              ? cardStockData.data.map((p) => p.product_id)
                              : []
                            : []
                        );
                      }}
                    />
                  </th>
                  <th className="p-2 whitespace-nowrap w-[30vw]">
                    <div
                      className="flex items-center justify-between gap-4 whitespace-nowrap cursor-pointer hover:text-gray-400"
                      onClick={() =>
                        setReqBody((prevVal) => ({
                          ...prevVal,
                          sort:
                            prevVal.sort > 2 ? 1 : prevVal.sort === 1 ? 2 : 1,
                        }))
                      }
                    >
                      <p>Nama Produk</p>
                      {reqBody.sort === 1 ? (
                        <FaSortUp />
                      ) : reqBody.sort === 2 ? (
                        <FaSortDown />
                      ) : (
                        <FaSort />
                      )}
                    </div>
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Stok Awal
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Stok Masuk
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Stok Keluar
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Stok Akhir
                  </th>
                  <th className="p-2 text-left whitespace-nowrap">Aktivitas</th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Pembaruan
                  </th>
                </thead>
                <tbody>
                  {cardStockData &&
                  !isCardStockDataLoading &&
                  cardStockData.data &&
                  cardStockData.count > 0 ? (
                    cardStockData.data.map((data, index) => {
                      return (
                        <>
                          <tr
                            className={`border-b-2 cursor-pointer hover:bg-gray-100`}
                            onClick={() => {
                              const index = selectedStock.findIndex(
                                (stock) => stock === data.product_id
                              );
                              setSelectedStock(
                                index < 0
                                  ? [...selectedStock, data.product_id]
                                  : [
                                      ...selectedStock.slice(0, index),
                                      ...selectedStock.slice(index + 1),
                                    ]
                              );
                            }}
                          >
                            <td className="p-2 text-left">
                              <Checkbox
                                checked={selectedStock.includes(
                                  data.product_id
                                )}
                                onChange={() => {
                                  const index = selectedStock.findIndex(
                                    (stock) => stock === data.product_id
                                  );
                                  setSelectedStock(
                                    index < 0
                                      ? [...selectedStock, data.product_id]
                                      : [
                                          ...selectedStock.slice(0, index),
                                          ...selectedStock.slice(index + 1),
                                        ]
                                  );
                                }}
                              />
                            </td>
                            <td
                              className="p-2 text-left cursor-pointer hover:text-gray-400 text-[#071B82] w-[30vw]"
                              onClick={() => {
                                navigate(
                                  `${
                                    isWarehouse ? "/central-warehouse" : ""
                                  }/inventory/card-stock/card-stock-detail-new/${
                                    data.product_id
                                  }`
                                );
                              }}
                            >
                              <div>
                                <p className=" font-bold">
                                  {data.product_name}
                                </p>
                                <p>ID Produk: {data.product_id}</p>
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              {data.stock_current}
                            </td>
                            <td className="p-2 text-center">{data.stock_in}</td>
                            <td className="p-2 text-center">
                              {data.stock_out}
                            </td>
                            <td className="p-2 text-center">
                              {data.final_stock}
                            </td>
                            <td
                              className="p-2 text-center"
                              style={{
                                color: `${stringToColour(data.status)}`,
                              }}
                            >
                              <div className="whitespace-nowrap flex items-center gap-2">
                                <FaCircle />
                                <p>{data.status}</p>
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              {moment(data.created_at).format("DD MMM YYYY")}
                            </td>
                          </tr>
                        </>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-2 text-center border-b">
                        {isCardStockDataLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <div className="py-5">
                            <p className="text-[18px] font-semibold mb-2">
                              Tidak ada hasil
                            </p>
                            <p className="mb-4">
                              Ubah filter atau kata kunci untuk menemukan produk
                            </p>
                            <button
                              type="button"
                              className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-fit py-2 px-4 text-[14px] whitespace-nowrap"
                              onClick={() => {
                                setReqBody({
                                  ...reqBody,
                                  warehouse: isWarehouse ? isWarehouse : false,
                                  product_name: "",
                                  start_date: "",
                                  end_date: "",
                                  period: 0,
                                  sort: 1,
                                  offset: 1,
                                });
                                mutateCardStockData({
                                  ...reqBody,
                                  warehouse: isWarehouse ? isWarehouse : false,
                                  product_name: "",
                                  start_date: "",
                                  end_date: "",
                                  period: 0,
                                  sort: 1,
                                  offset: 1,
                                });
                                setSelectedStock([]);
                              }}
                            >
                              {cardStockData && cardStockData.count === 0
                                ? "Hapus Filter"
                                : "Muat Ulang"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {cardStockData && !isCardStockDataLoading && cardStockData.count > 0 ? (
          <JumpPagePagination
            offset={reqBody.offset}
            limit={reqBody.limit}
            setOffset={(e) =>
              setReqBody((prevVal) => ({ ...prevVal, offset: e }))
            }
            setLimit={(e) => {
              setReqBody((prevVal) => ({ ...prevVal, limit: e }));
            }}
            total={cardStockData.count}
            canChangeLimit={true}
          />
        ) : null}
      </div>

      <Popover
        open={Boolean(anchorElExport)}
        anchorEl={anchorElExport}
        onClose={() => setAnchorElExport(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div className="w-full">
          <p
            className={`px-8 py-2 border-b-2  ${
              selectedStock.length === 0
                ? "bg-gray-300 text-white cursor-default"
                : "cursor-pointer hover:bg-gray-100"
            }`}
            onClick={() => {
              if (selectedStock.length > 0) handleExport();
            }}
          >
            Stok Terpilih
          </p>
          <p
            className="px-8 py-2  cursor-pointer hover:bg-gray-100"
            onClick={handleExportAll}
          >
            Semua Stok
          </p>
        </div>
      </Popover>

      <Popover
        open={Boolean(anchorElPrint)}
        anchorEl={anchorElPrint}
        onClose={() => setAnchorElPrint(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div className="w-full">
          <p
            className={`px-8 py-2 border-b-2 ${
              selectedStock.length === 0
                ? "cursor-default bg-gray-300 text-white"
                : "cursor-pointer hover:bg-gray-100"
            }`}
            onClick={() => {
              if (selectedStock.length > 0) {
                setAnchorElPrint(null);
                setIsPrint(true);
                mutatePrint({
                  product_id: selectedStock,
                  warehouse: isWarehouse ? isWarehouse : false,
                });
              }
            }}
          >
            Stok Terpilih
          </p>
          <p
            className="px-8 py-2  cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setAnchorElPrint(null);
              setIsPrint(true);
              mutatePrint({
                product_id: [],
                warehouse: isWarehouse ? isWarehouse : false,
              });
            }}
          >
            Semua Stok
          </p>
        </div>
      </Popover>

      <Popover
        open={Boolean(anchorElPeriod)}
        anchorEl={anchorElPeriod}
        onClose={() => setAnchorElPeriod(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div className="py-2">
          <RadioGroup
            value={reqBody.period}
            onChange={(e) => {
              const numberVal = Number(e.target.value);
              setSelectedStock([]);
              setReqBody((prevValue) => ({
                ...prevValue,
                period: numberVal,
                start_date: "",
                end_date: "",
              }));
            }}
          >
            {PERIOD.map((item, i) => (
              <div className={`px-4 text-[12px]`} key={i}>
                <FormControlLabel
                  value={item.value}
                  label={item.name}
                  control={<Radio sx={{ fontSize: 12 }} />}
                  sx={{ width: "100%", maxWidth: "200px", fontSize: 12 }}
                />
                {item.value === 7 ? (
                  <div>
                    <DesktopDatePicker
                      disabled={reqBody.period !== 7}
                      fullWidth
                      onChange={(newDate) =>
                        setReqBody((prevVal) => ({
                          ...prevVal,
                          start_date: newDate ? toApiDate(newDate) : "",
                        }))
                      }
                      value={
                        reqBody.start_date && reqBody.start_date !== ""
                          ? reqBody.start_date
                          : null
                      }
                      renderInput={(params) => {
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                          />
                        );
                      }}
                    />
                    <DesktopDatePicker
                      fullWidth
                      disabled={reqBody.period !== 7}
                      onChange={(newDate) =>
                        setReqBody((prevVal) => ({
                          ...prevVal,
                          end_date: newDate ? toApiDate(newDate) : "",
                        }))
                      }
                      value={
                        reqBody.end_date && reqBody.end_date !== ""
                          ? reqBody.end_date
                          : null
                      }
                      renderInput={(params) => {
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                          />
                        );
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </RadioGroup>
        </div>
      </Popover>

      <Popover
        open={Boolean(anchorElSort)}
        anchorEl={anchorElSort}
        onClose={() => setAnchorElSort(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div className="py-2">
          <RadioGroup
            value={reqBody.sort}
            onChange={(e) => {
              const numberVal = Number(e.target.value);
              setSelectedStock([]);
              setReqBody((prevValue) => ({
                ...prevValue,
                sort: numberVal,
              }));
            }}
          >
            {SORTBY.map((item, i) => (
              <div className={`px-4 text-[12px]`} key={i}>
                <FormControlLabel
                  value={item.value}
                  label={item.name}
                  control={<Radio sx={{ fontSize: 12 }} />}
                  sx={{
                    width: "100%",
                    fontSize: 12,
                  }}
                />
              </div>
            ))}
          </RadioGroup>
        </div>
      </Popover>

      <div className="hidden">
        <PrintCardStock
          printRef={printRef}
          dataArr={dataPrint && !isLoadingPrint ? dataPrint : [""]}
          dataLabel={dataLabel ? dataLabel : null}
        />
      </div>

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default CardStockNew;
